"use client";
import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";

// 自定义 Part 项配置
export interface PartItemConfig {
	titleKey: string;
	descKey: string;
	image: string;
}

export interface PartProps {
	// 翻译命名空间
	translationNamespace?: string;
	// 自定义标题（优先级高于翻译）
	title?: string;
	// 自定义副标题（优先级高于翻译）
	subtitle?: string;
	// 自定义卡片配置（图片路径 + 翻译键）
	customItems?: PartItemConfig[];
	// 是否显示副标题
	showSubtitle?: boolean;
}

// CNC Milling 默认配置
const DEFAULT_MILLING_ITEMS: PartItemConfig[] = [
	{
		titleKey: "item1Title",
		descKey: "item1Description",
		image: "/image/new-version/machining/cnc-milling/part/3-axis-cnc-milling​.webp",
	},
	{
		titleKey: "item2Title",
		descKey: "item2Description",
		image: "/image/new-version/machining/cnc-milling/part/4-axis-cnc-milling.webp",
	},
	{
		titleKey: "item3Title",
		descKey: "item3Description",
		image: "/image/new-version/machining/cnc-milling/part/5-axis-cnc-milling​.webp",
	},
];

const Part = ({
	translationNamespace = "cncMillingPart",
	title,
	subtitle,
	customItems,
	showSubtitle = true,
}: PartProps) => {
	const t = useTranslations(translationNamespace);
	const items = customItems || DEFAULT_MILLING_ITEMS;

	// 获取显示的标题和副标题（自定义优先）
	const displayTitle = title || t("title");
	const displaySubtitle = subtitle || t("subtitle");

	// 根据 item 数量决定网格列数
	const gridColsClass =
		items.length === 4
			? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
			: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{displayTitle}
					</h2>
					{showSubtitle && displaySubtitle && (
						<p className="text-sm leading-relaxed text-gray-900 md:text-base">{displaySubtitle}</p>
					)}
				</div>

				{/* 卡片区域 */}
				<div className={`grid ${gridColsClass} gap-6 lg:gap-8`}>
					{items.map((item, index) => (
						<div
							key={index}
							className="group overflow-hidden rounded-lg border border-[#7d7d7d] bg-white transition-shadow duration-300"
						>
							{/* 图片区域 */}
							<div className="relative h-[200px] w-full overflow-hidden md:h-[220px]">
								<Image
									src={item.image}
									alt={t(item.titleKey)}
									fill
									className="object-cover transition-transform duration-500 group-hover:scale-105"
									sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
								/>
							</div>

							{/* 内容区域 */}
							<div className="p-2 md:p-3">
								<h3 className="mb-3 text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(item.titleKey)}
								</h3>
								<p className="mb-5 text-sm leading-relaxed text-gray-900 md:text-base">{t(item.descKey)}</p>
								<Link
									href="/upload"
									className="mt-8 inline-flex items-center justify-center rounded bg-themePrimary400 px-5 py-2 text-sm font-medium text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white"
								>
									{t("getQuote")}
								</Link>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default Part;
